import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import CustomerSelection from "./steps/CustomerSelection";
import TemplateSelection from "./steps/TemplateSelection";
import PlanConfiguration from "./steps/PlanConfiguration";
import ReviewAndConfirm from "./steps/ReviewAndConfirm";
import { createPlanThunk } from "../../../../store/thunks/jewelleryPlan/planThunks";
import { JEWELLERY_ROUTES } from "../../../../constants/jewellery.constant";

const CreatePlanWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.jewelleryPlan?.plans);
  const { primaryBranch } = useSelector((state) => state.branch);

  const [currentStep, setCurrentStep] = useState(1);
  const [planData, setPlanData] = useState({
    userId: null,
    customer: null,
    templateId: null,
    template: null,
    monthlyAmount: null,
    startDate: new Date(),
    notes: "",
  });

  const steps = [
    { id: 1, name: "Customer", component: CustomerSelection },
    { id: 2, name: "Template", component: TemplateSelection },
    { id: 3, name: "Configuration", component: PlanConfiguration },
    { id: 4, name: "Review", component: ReviewAndConfirm },
  ];

  const handleNext = (stepData) => {
    setPlanData({ ...planData, ...stepData });
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const result = await dispatch(
        createPlanThunk({
          userId: planData.userId,
          templateId: planData.templateId,
          monthlyAmount: planData.monthlyAmount,
          startDate: planData.startDate,
          notes: planData.notes,
          branchId: primaryBranch?._id,
          branchCode: primaryBranch?.code,
        }),
      ).unwrap();

      // Navigate to the created plan
      navigate(`/jewellery/${result.data._id}`);
    } catch (err) {
      console.error("Failed to create plan:", err);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="max-w-4xl mx-auto sm:p-6 p-4">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <div className="relative">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 
                    ${
                      currentStep > step.id
                        ? "bg-success border-success text-text-on-primary"
                        : currentStep === step.id
                          ? "bg-primary border-primary text-text-on-primary"
                          : "bg-background border-neutral-light text-neutral-dark"
                    }`}
                >
                  {currentStep > step.id ? "✓" : step.id}
                </div>
                <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs whitespace-nowrap">
                  {step.name}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 
                    ${
                      currentStep > step.id ? "bg-success" : "bg-neutral-light"
                    }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="mt-12 bg-background shadow rounded-lg p-6">
        <CurrentStepComponent
          data={planData}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
};

export default CreatePlanWizard;
